// src/services/opexInvoiceService.js
import api from 'services/api';

export const opexInvoiceService = {
  // ========================================
  // OPEX INVOICE METHODS
  // ========================================

  // Get OPEX invoices (filtered by role automatically)
  getInvoices: async (params = {}) => {
    const response = await api.get('/opex-invoices', { params });
    return response.data;
  },

  // Get single OPEX invoice
  getInvoice: async (id) => {
    const response = await api.get(`/opex-invoices/${id}`);
    return response.data;
  },

  // Get OPEX invoice statistics (admin only)
  getStatistics: async () => {
    const response = await api.get('/opex-invoices/statistics');
    return response.data;
  },

  // Get vendor dashboard statistics (opex_vendor only)
  getVendorDashboard: async () => {
    const response = await api.get('/opex-invoices/vendor-dashboard');
    return response.data;
  },

  // Get available OPEX categories
  getCategories: async () => {
    const response = await api.get('/opex-invoices/categories');
    return response.data;
  },

  // ========================================
  // OPEX INVOICE CRUD OPERATIONS
  // ========================================

  // Create new OPEX invoice (opex_vendor only)
  createInvoice: async (data) => {
    const formData = new FormData();

    // Append regular fields
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined && key !== 'invoice_file') {
        formData.append(key, data[key]);
      }
    });

    // Append file only if it's a File object
    if (data.invoice_file instanceof File) {
      formData.append('invoice_file', data.invoice_file);
    }

    const response = await api.post('/opex-invoices', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Update OPEX invoice (opex_vendor only)
  updateInvoice: async (id, data) => {
    const formData = new FormData();

    // Add _method for Laravel to handle PUT request with multipart/form-data
    formData.append('_method', 'PUT');

    // Append regular fields
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined && key !== 'invoice_file') {
        formData.append(key, data[key]);
      }
    });

    // Append file if provided
    if (data.invoice_file instanceof File) {
      formData.append('invoice_file', data.invoice_file);
    }

    // Use POST with _method=PUT for multipart/form-data compatibility
    const response = await api.post(`/opex-invoices/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Delete OPEX invoice (opex_vendor only)
  deleteInvoice: async (id) => {
    const response = await api.delete(`/opex-invoices/${id}`);
    return response.data;
  },

  // Submit OPEX invoice for approval (opex_vendor only)
  submitInvoice: async (id) => {
    const response = await api.post(`/opex-invoices/${id}/submit`);
    return response.data;
  },

  // ========================================
  // APPROVAL OPERATIONS
  // ========================================

  // Approve OPEX invoice (admin only)
  approveInvoice: async (id, comment = '') => {
    const response = await api.post(`/opex-invoices/${id}/approve`, { comment });
    return response.data;
  },

  // Reject OPEX invoice (admin only)
  rejectInvoice: async (id, comment) => {
    const response = await api.post(`/opex-invoices/${id}/reject`, { comment });
    return response.data;
  },

  // Mark OPEX invoice as paid (admin only)
  markAsPaid: async (id, comment = '') => {
    const response = await api.post(`/opex-invoices/${id}/mark-as-paid`, { comment });
    return response.data;
  },

  // ========================================
  // FILE OPERATIONS
  // ========================================

  // Download invoice file
  downloadInvoiceFile: async (id) => {
    const response = await api.get(`/opex-invoices/${id}/download-invoice`, {
      responseType: 'blob'
    });
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'opex_invoice.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[*]?=['"]?([^'"\n;]+)['"]?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    return { blob: response.data, filename };
  },

  // ========================================
  // COMMENT OPERATIONS
  // ========================================

  // Get invoice comments
  getComments: async (id) => {
    const response = await api.get(`/invoices/${id}/comments`);
    return response.data;
  },

  // Add comment to invoice
  addComment: async (id, comment) => {
    const response = await api.post(`/invoices/${id}/comments`, { comment });
    return response.data;
  },

  // ========================================
  // UTILITY METHODS
  // ========================================

  // Helper: Download file with proper filename
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export default opexInvoiceService;
